// The Curator API contract: every route under /api/v1/, with its parameters,
// request body, responses and failures, declared once as Effect HttpApi
// endpoints, and CuratorApi, the API a client calls. The Exchange serves the
// same endpoints with its own Bearer middleware (src/exchange/curator-api.ts),
// and openapi/curator-api.json is generated from them. It imports only effect,
// the Tool SDK and its own modules, so a client outside the Worker, such as
// the Curator CLI, carries only the wire (test/boundary.test.ts). See Design >
// Names and definitions, "Curator API contract", and Design > Exchange,
// "Curator API".

import { Effect, Option, Redacted, Schema, SchemaTransformation, Struct } from "effect";
import { HttpApi, HttpApiEndpoint, HttpApiError, HttpApiGroup, HttpApiMiddleware, HttpApiSchema, HttpApiSecurity, OpenApi } from "effect/http-api";
import { ACTIVITY_LIMIT, ActionId, AgentActionTranscript, AgentActivityResponse, AgentTrajectory, CuratorReplyPending, CuratorReplyResponse, MarkInteractionsRead, RequestRepairResponse, RequeueResponse, WaitingInteractionsResponse } from "./agent-activity.ts";
import { HubAgentSwitchState, SetHubAgentSwitch } from "./hub-agent-switches.ts";
import { HubEvent } from "./hub-events.ts";
import { CuratorReply, InteractionId } from "./interaction.ts";
import * as Errors from "./errors.ts";
import { ApiKeyAccount, ApiKeyHeader, ApiKeyParam, ApiKeyPrefix, ClientCredentialsGrant, JwtBearerGrant, KeyExchangeGrant, RefreshTokenGrant } from "./api-key-credentials.ts";
import { KEY_KIND_OF_ALG, pemKeyOf } from "./pem-key.ts";
import { ApiKeyState, Blocker, CommitSha, HubId, HubJobId, HubJobKind, HubName, HubSecretState, HubSecretsSummary, Listing, StoredCommitSha, TestRunId, ToolsetStatus } from "./ids.ts";
import { GoldenComparison, GoldenComparisonCounts, TestResult, TestRunKind } from "./test-runs.ts";
import { ReadsRequest, ReadsResponse, WireThing } from "./warmhub-reads.ts";
import { GoldenQuestion, HubManifest } from "../tool-sdk/manifest.ts";
import { MAX_INTENTS } from "../tool-sdk/limits.ts";
import { Json } from "../tool-sdk/json.ts";

// What a client of this contract needs besides its schemas: the branded IDs
// its calls take, the states its responses carry, and the JSON type of an
// event's detail.
export { CommitSha, HubId, TestRunId } from "./ids.ts";
export type { ApiKeyState, Blocker, HubSecretState, HubSecretsSummary, Listing } from "./ids.ts";
export { TestRunKind } from "./test-runs.ts";
export type { Json } from "../tool-sdk/json.ts";

export const TestRunSummary = Schema.Struct({
  id: Schema.String,
  kind: TestRunKind,
  status: Schema.Literals(["running", "passed", "failed", "error"]),
  passed: Schema.NullOr(Schema.Int),
  total: Schema.NullOr(Schema.Int),
  startedAt: Schema.Int,
  finishedAt: Schema.NullOr(Schema.Int),
}).annotate({ identifier: "TestRunSummary" });
export type TestRunSummary = typeof TestRunSummary.Type;

export const ToolsetSummary = Schema.Struct({
  commit: StoredCommitSha,
  status: ToolsetStatus,
  problems: Schema.Array(Schema.String),
  sdkVersion: Schema.NullOr(Schema.String),
  stagedBy: Schema.NullOr(Schema.String),
  createdAt: Schema.Int,
  activatedAt: Schema.NullOr(Schema.Int),
  bundles: Schema.Int,
  // The latest run of each kind.
  runs: Schema.Struct({ examples: Schema.NullOr(TestRunSummary), golden: Schema.NullOr(TestRunSummary) }),
  // For a toolset that isn't active, true exactly when `blockers` is empty.
  // The active toolset has no blockers and can't be activated again.
  canActivate: Schema.Boolean,
  blockers: Schema.Array(Blocker),
  // For a toolset that isn't active, the counts of its latest golden run's
  // comparison with the active toolset's, when both ended passed or failed;
  // null otherwise. Advice, not a blocker.
  comparison: Schema.NullOr(GoldenComparisonCounts),
  // The state of the hub's WarmHub token for this toolset, never its value:
  // Expiring within 14 days of `expiresAt`, when a curator grants it again.
  // See Design > Operations, "Credential check".
  secret: HubSecretState,
}).annotate({ identifier: "ToolsetSummary" });
export type ToolsetSummary = typeof ToolsetSummary.Type;

// One activation of a toolset, a rollback included: the toolset activated,
// the one it replaced (null for the hub's first activation, or when none was
// recorded: rows copied from before migration 0013 have no replaced
// toolset), and when. See Design > Exchange, "Activation".
export const ActivationSummary = Schema.Struct({
  commit: StoredCommitSha, replacedCommit: Schema.NullOr(StoredCommitSha), at: Schema.Int,
}).annotate({ identifier: "ActivationSummary" });
export type ActivationSummary = typeof ActivationSummary.Type;

// A hub's answers, refunds, revenue, outcomes and latency.
const HubStats = Schema.Struct({
  answers: Schema.Int,
  refunds: Schema.Int,
  revenueNano: Schema.Finite,
  outcomes: Schema.Record(Schema.String, Schema.Int),
  p50Ms: Schema.NullOr(Schema.Finite),
  p95Ms: Schema.NullOr(Schema.Finite),
}).annotate({ identifier: "HubStats" });

// The hub record, as the Curator API's writes return it. See Design >
// HubRouter, "Toolsets".
const HubRecordJson = Schema.Struct({
  id: HubId,
  name: HubName,
  toolsetRepo: Schema.String,
  audience: Schema.Union([
    Schema.Struct({ kind: Schema.Literal("everyone") }),
    Schema.Struct({ kind: Schema.Literal("org"), orgId: Schema.String }),
  ]),
  listing: Listing,
  listingNote: Schema.NullOr(Schema.String),
}).annotate({ identifier: "HubRecord" });

// One hub, as GET /api/v1/hubs and GET /api/v1/listing-approvals return it.
// See Design > Exchange, "Curator API".
export const HubSummary = Schema.Struct({
  hub: HubRecordJson,
  // The manifest of the toolset in use: the active one, or, for a hub with
  // none active (a new hub, or an archived one), its newest toolset, which
  // for an archived hub may be one that was once active. Its version is that
  // toolset's commit. Never null: these routes list only hubs with a
  // toolset.
  manifest: HubManifest,
  active: Schema.NullOr(ToolsetSummary),
  // The newest staged toolset that was never active and is newer than the
  // active one: what a test run with `x-toolsets: staged` reaches.
  newestStaged: Schema.NullOr(ToolsetSummary),
  stats: Schema.NullOr(HubStats),
  // Why a hub limited to an org reaches no consumer, or null when nothing
  // stops it: its linked repository reads public (PublicRepository), or its
  // visibility can't be read as the hub (VisibilityUnread). Always null for a
  // hub whose audience is everyone. See Design > Exchange, "Hub audiences".
  audienceBlocked: Schema.NullOr(Schema.Literals(["PublicRepository", "VisibilityUnread"])),
}).annotate({ identifier: "HubSummary" });
export type HubSummary = typeof HubSummary.Type;

// ---------------------------------------------------------------- failures

// Each failure a route declares is a domain error, encoded as it is: its
// tag, its fields and its authored detail, with the status its class
// declares (./errors.ts). A service's failure is declared in its
// public form, ServiceUnavailable or ServiceRefused, which every handler
// maps it to (publicServiceFailure). RateLimited and ServiceUnavailable send
// their Retry-After as a response header. The request's ID is in every
// response's x-request-id header, so no body repeats it. See Design >
// Errors, "Public error bodies".
// The wait goes in the Retry-After header alone, so the body is the error
// without it.
export const RateLimitedBody = Schema.Struct(Struct.omit(Errors.RateLimited.fields, ["retryAfterSeconds"])).pipe(HttpApiSchema.status(429));
const RateLimited = Errors.RateLimited.pipe(HttpApiSchema.encodeToWithHeaders({ body: RateLimitedBody, headers: { "retry-after": Schema.FiniteFromString } }, {
  decode: ({ body, headers }) => new Errors.RateLimited({ ...body, retryAfterSeconds: headers["retry-after"] }),
  encode: ({ retryAfterSeconds, ...body }) => ({ body, headers: { "retry-after": retryAfterSeconds } }),
}));
export const ServiceUnavailableBody = Schema.Struct(Struct.omit(Errors.ServiceUnavailable.fields, ["retryAfterSeconds"])).pipe(HttpApiSchema.status(503));
const ServiceUnavailable = Errors.ServiceUnavailable.pipe(HttpApiSchema.encodeToWithHeaders({ body: ServiceUnavailableBody, headers: { "retry-after": Schema.optionalKey(Schema.FiniteFromString) } }, {
  decode: ({ body, headers }) => new Errors.ServiceUnavailable({ ...body, ...(headers["retry-after"] === undefined ? {} : { retryAfterSeconds: headers["retry-after"] }) }),
  encode: ({ retryAfterSeconds, ...body }) => ({ body, headers: retryAfterSeconds === undefined ? {} : { "retry-after": retryAfterSeconds } }),
}));
// A defect, which the router answers outside any route's program: the
// Exchange's InternalError, with the request's ID, which its log line
// carries too. Declared for every route. See Design > Errors, "Defects".
const InternalError = Schema.Struct({
  _tag: Schema.Literal("InternalError"), requestId: Schema.String, retryable: Schema.Literal(false), detail: Schema.String,
}).pipe(HttpApiSchema.status(500), Schema.annotate({ identifier: "InternalError" }));
const {
  ActivationRefused, AgentNotAllowed, ApiKeyLimitReached, AudienceRefused, HubAgentNotConnected, HubAlreadyRegistered, HubArchived, HubNotFound, HubRepoConflict, HubSecretMissing, HubTokenRefused, IntentIndexRebuildRefused, InteractionClosed, InvalidListingTransition,
  NotFound, NotOperator, NotOperatorOrgMember, NotRepoConfigurer, ReplyInvalid, ServiceRefused, ToolsetChanged, ToolsetNotFound, Unauthenticated,
} = Errors;

// What every route can fail with, which the Bearer middleware declares for
// each: its checks (the token, the operator org, the rate limit), a service
// failing, which any handler may meet too, and a defect; and on the agent
// path, the hub agent's refusals: an operation it may not call, and a hub
// whose Hub token can't be used. See Design > Exchange, "The agent path".
export const BearerFailures = [Unauthenticated, NotOperatorOrgMember, RateLimited, ServiceUnavailable, ServiceRefused, AgentNotAllowed, HubAgentNotConnected, InternalError] as const;

// A route about one hub, named in its path by its Hub ID. A route that
// decodes more than a Hub ID also declares Effect's empty 400 for a request
// that doesn't decode (HttpApiError.BadRequestNoContent). See Design >
// Errors, "A request that doesn't decode is a 400".
const OneHub = [HubNotFound] as const;

// A routing check: the parts of a draft hub manifest that intent routing
// and parameter resolution read, its scope, what it doesn't hold, its
// intents and, optionally, its entities, and the questions to route on it.
// The hub agent's Intents build stage sends it before a manifest exists
// anywhere else, and its Tools build stage, with the draft's entities, to
// learn each test case's parameters as a tool receives them; a curator
// sends it with wh-curator routing-check. See Design > Hub agent, "The route
// check is the HubRouter's own".
/** The most questions one routing check routes. */
export const MAX_ROUTING_CHECK_QUESTIONS = 100;
export const RoutingCheckRequest = Schema.Struct({
  manifest: Schema.Struct({
    scope: HubManifest.fields.scope,
    notInScope: HubManifest.fields.notInScope,
    intents: HubManifest.fields.intents.check(Schema.isMinLength(1), Schema.isMaxLength(MAX_INTENTS)),
    entities: Schema.optionalKey(HubManifest.fields.entities),
  }),
  questions: Schema.Array(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500))).check(Schema.isMinLength(1), Schema.isMaxLength(MAX_ROUTING_CHECK_QUESTIONS)),
});
export type RoutingCheckRequest = typeof RoutingCheckRequest.Type;
/**
 * One question as intent routing routed it on the draft: to an intent, to a
 * decline with the router model's reason, to a name the draft doesn't
 * declare, or unrouted, when its router model call failed, with a reason the
 * Exchange wrote. A question routed to an intent carries its parameters as
 * parameter resolution gives them to the intent's tool (a ResolvedParam,
 * src/tool-sdk/types.ts), an entity parameter resolved against the draft's
 * entities, and each required parameter that's missing or didn't resolve,
 * with the reason the HubRouter would answer MissingParameters with, or a
 * reason the Exchange wrote when the lookup failed (decision
 * route-check-per-question-failures).
 */
export const RoutedQuestion = Schema.Union([
  Schema.TaggedStruct("Intent", {
    question: Schema.String,
    intent: Schema.String,
    params: Schema.Record(Schema.String, Schema.Struct({ name: Schema.String, raw: Schema.String, thing: Schema.optionalKey(WireThing), choice: Schema.optionalKey(Schema.String) })),
    missing: Schema.Array(Schema.Struct({ name: Schema.String, reason: Schema.String })),
  }),
  Schema.TaggedStruct("Decline", { question: Schema.String, reason: Schema.String }),
  Schema.TaggedStruct("UnknownIntent", { question: Schema.String, name: Schema.String }),
  Schema.TaggedStruct("Unrouted", { question: Schema.String, reason: Schema.String }),
]);
export type RoutedQuestion = typeof RoutedQuestion.Type;
/** Each question's routing, in the order asked, and what the check's router model calls cost, in US dollars. */
export const RoutingCheckResponse = Schema.Struct({ results: Schema.Array(RoutedQuestion), costUsd: Schema.Finite });
export type RoutingCheckResponse = typeof RoutingCheckResponse.Type;

// A hub job and each hub's step, as the hub job ledger records them. See
// Design > Operations, "Hub jobs".
const HubJobJson = Schema.Struct({
  id: Schema.String, kind: HubJobKind, startedBy: Schema.String, createdAt: Schema.Int,
  counts: Schema.Struct({ pending: Schema.Int, done: Schema.Int, failed: Schema.Int }),
  hubs: Schema.Array(Schema.Struct({ id: Schema.String, status: Schema.Literals(["pending", "done", "failed"]), attempts: Schema.Int, detail: Schema.NullOr(Schema.String), updatedAt: Schema.Int })),
});

// --------------------------------------------------------------- responses

export const HubsResponse = Schema.Struct({
  settings: Schema.Struct({
    maxWords: Schema.Int,
    maxAnswers: Schema.Int,
    distanceCutoff: Schema.Finite,
    dispatchThresholdFloor: Schema.Finite,
    defaultDispatchThreshold: Schema.Finite,
  }),
  hubs: Schema.Array(HubSummary),
  // The caller's hubs that have no toolset yet, such as one registered a
  // moment ago, which `hubs` leaves out because a summary needs a manifest.
  hubsWithoutToolset: Schema.Array(HubRecordJson),
}).annotate({ identifier: "HubsResponse" });

const DemandList = Schema.Struct({
  items: Schema.Array(Schema.Struct({
    question: Schema.String,
    count: Schema.Int,
    bidUsd: Schema.Finite,
    unpricedCount: Schema.Int,
    lastAt: Schema.Int,
    distance: Schema.NullOr(Schema.Finite),
    rank: Schema.NullOr(Schema.Int),
    hubScore: Schema.NullOr(Schema.Finite),
    reason: Schema.NullOr(Schema.String),
  })),
  questions: Schema.Int,
  distinct: Schema.Int,
  bidUsd: Schema.Finite,
});

// See Design > Exchange, "Events and demand".
export const DemandResponse = Schema.Struct({
  hub: Schema.String,
  days: Schema.Int,
  cutoff: Schema.Finite,
  nearMissMargin: Schema.Finite,
  threshold: Schema.Finite,
  floor: Schema.Finite,
  declined: DemandList,
  missed: DemandList,
  outranked: DemandList,
}).annotate({ identifier: "DemandResponse" });

// One event as a curator reads it: today, every column of the D1 table
// `events`, by its column name, with candidates and detail as the JSON they
// hold. Which of these a curator should see is warmhub/hub#454; until then
// test/curator-api-contract.test.ts holds its keys to the table's.
export const Event = Schema.Struct({
  request_id: Schema.String, question_id: Schema.String, question: Schema.String, hub: Schema.NullOr(Schema.String), outcome: Schema.String,
  intent: Schema.NullOr(Schema.String), candidates: Schema.NullOr(Json), ms: Schema.NullOr(Schema.Finite), detail: Schema.NullOr(Json),
  test_run: Schema.Int, created_at: Schema.Int, bid_nano: Schema.NullOr(Schema.Finite), distance: Schema.NullOr(Schema.Finite),
  match_rank: Schema.NullOr(Schema.Int), hub_score: Schema.NullOr(Schema.Finite), order_score: Schema.NullOr(Schema.Finite),
  dispatch_threshold: Schema.NullOr(Schema.Finite), score_cached: Schema.NullOr(Schema.Int), selection: Schema.NullOr(Schema.String),
}).annotate({ identifier: "Event" });
export type Event = typeof Event.Type;
export const EventsResponse = Schema.Struct({ events: Schema.Array(Event) }).annotate({ identifier: "EventsResponse" });

// See Design > Toolsets and tools, "Golden question sets".
export const GoldenResponse = Schema.Struct({
  questions: Schema.Array(Schema.Struct({
    hub: Schema.String,
    question: Schema.String,
    expectHub: Schema.NullOr(Schema.String),
    expectIntent: GoldenQuestion.fields.expectIntent,
    expectOutcome: Schema.String,
    note: Schema.String,
  })),
}).annotate({ identifier: "GoldenResponse" });

// One test run with each question's result, null until the run ends, and a
// golden run's comparison with the active toolset's, null for an examples
// run. See Design > Exchange, "Test runs".
const TestRun = Schema.Struct({ run: TestRunSummary, commit: Schema.String, results: Schema.NullOr(Schema.Array(TestResult)), comparison: Schema.NullOr(GoldenComparison) });

// The request bodies. Each is required, though all its fields may be
// optional: a request with nothing to say sends {}. See Design > Exchange,
// "Curator API".
// A listing change's or an archive's optional reason, which the hub's curators see.
const WithReason = Schema.Struct({ reason: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(500))) });
// A commit SHA is 40 or 64 hex characters; nothing longer is a commit.
// A grant names the toolset only: the Exchange mints the Hub token again with
// the caller's bearer. It takes no token of the caller's own, so no grant adds
// a toolset token. See Design > Operations, "The HubDurableObject".
const GrantRequest = Schema.Struct({ commit: Schema.String.check(Schema.isMaxLength(64)) });
// A host an API key is for: a lowercase DNS name, as a URL's hostname gives
// it, with no port, path or scheme. See Design > Exchange, "Curator API".
export const ApiKeyHost = Schema.String.check(Schema.isPattern(/^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/));
// A secret that goes in a header, a static key or a key_exchange key:
// printable ASCII with no line break, so it can't add another header.
const HeaderSecret = Schema.Redacted(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(4096), Schema.isPattern(/^[\x20-\x7e]+$/)));
// A secret that goes in an OAuth 2.0 form or Basic auth: a client's ID or
// secret, or a refresh token. Each secret has its own rule, so a grant whose
// secret is, say, a PEM key can take line breaks without loosening another's.
const FormSecret = Schema.Redacted(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(4096), Schema.isPattern(/^[\x20-\x7e]+$/)));
// A jwt_bearer grant's private key: PEM, so its lines break, and at most
// 8 KiB, which an RSA key of 4096 bits fits.
const PemSecret = Schema.Redacted(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(8192), Schema.isPattern(/^[\x20-\x7e\r\n]+$/)));
// Where the API key, or a grant's API key token, goes in a request to the
// host: in header `name`, after `prefix`, or in query parameter `name`,
// URL-encoded. See Design > Toolsets and tools, "EgressGateway".
const ApiKeySend = Schema.Union([
  Schema.Struct({ in: Schema.Literal("header"), name: ApiKeyHeader, prefix: Schema.optionalKey(ApiKeyPrefix) }),
  Schema.Struct({ in: Schema.Literal("query"), name: ApiKeyParam }),
]);
// A static credential's value, sent as it is; or a grant credential, the
// recipe the EgressGateway runs for an API key token and the secrets it
// takes, which must be the grant type's own. See Design > Names and
// definitions, "Static credential" and "Grant credential".
const ApiKeyCredential = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("static"), value: HeaderSecret }),
  Schema.Struct({ kind: Schema.Literal("grant"), grant: KeyExchangeGrant, secrets: Schema.Struct({ key: HeaderSecret }) }),
  Schema.Struct({ kind: Schema.Literal("grant"), grant: ClientCredentialsGrant, secrets: Schema.Struct({ clientId: FormSecret, clientSecret: FormSecret }) }),
  Schema.Struct({ kind: Schema.Literal("grant"), grant: RefreshTokenGrant, secrets: Schema.Struct({ clientId: FormSecret, clientSecret: Schema.optionalKey(FormSecret), refreshToken: FormSecret }) })
    .check(Schema.makeFilter((c) => c.grant.clientAuth === "none" || c.secrets.clientSecret !== undefined || "clientSecret is required unless clientAuth is none")),
  // The key must read as PEM, and be the kind its algorithm signs with.
  Schema.Struct({ kind: Schema.Literal("grant"), grant: JwtBearerGrant, secrets: Schema.Struct({ privateKey: PemSecret }) })
    .check(Schema.makeFilter((c) => Option.exists(pemKeyOf(Redacted.value(c.secrets.privateKey)), (k) => k.kind === KEY_KIND_OF_ALG[c.grant.alg])
      || `privateKey must be an unencrypted PEM key for ${c.grant.alg}`)),
]);
// An API key: which of the host's accounts it is for ("default" when it
// names none), where it goes, its credential, and, in ms, when the
// credential itself expires, as the curator chose it at its host. See
// Design > Exchange, "Curator API".
export const ApiKeyRequest = Schema.Struct({
  account: Schema.optionalKey(ApiKeyAccount),
  send: ApiKeySend,
  credential: ApiKeyCredential,
  expiresAt: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThan(0))),
});
const StageRequest = Schema.Struct({ commit: Schema.Union([Schema.Literal("main"), CommitSha]) });
const RegisterRequest = Schema.Struct({ repo: Schema.String.check(Schema.isMaxLength(200)) });
const PushTokenRequest = Schema.Struct({ ttlSeconds: Schema.optionalKey(Schema.Int) });
const TestRunRequest = Schema.Struct({ kind: TestRunKind });
// An override takes a reason, and a reason is only for an override. See
// Design > Exchange, "Activation".
const ActivateRequest = Schema.Struct({
  override: Schema.optionalKey(Schema.Boolean),
  reason: Schema.optionalKey(Schema.String.check(Schema.isPattern(/\S/), Schema.isMaxLength(500))
    .annotate({ description: "Why the operator activates past the blockers: 1 to 500 characters, required with override." })),
}).check(Schema.makeFilter((r) => (r.override === true) === (r.reason !== undefined) || "an override takes a reason, and a reason is only for an override"));

// Every route needs a WarmHub bearer token, sent as `authorization: Bearer`,
// and can fail with BearerFailures. A client declares the token here; the
// Exchange serves the same endpoints with its own Bearer middleware, which
// makes the checks and provides its handlers' services. See Design >
// Exchange, "Bearer tokens".
export class Bearer extends HttpApiMiddleware.Service<Bearer>()("answer-exchange/contract/Bearer", {
  security: { bearer: HttpApiSecurity.bearer },
  error: BearerFailures,
}) {}

// The query parameters, which the router decodes like the rest of the
// request: a value these schemas don't accept is a 400, never clamped or
// ignored. A whole number in the query, from `minimum` to `maximum`, and
// `byDefault` when it's left out.
// The spec shows the string as the query carries it, with the range and
// default in its description.
const wholeNumber = (minimum: number, maximum: number, byDefault: number, description: string) =>
  Schema.String.check(Schema.isPattern(/^[0-9]+$/))
    .annotate({ description: `${description}: a whole number from ${minimum} to ${maximum}, ${byDefault} by default.` })
    .pipe(Schema.decodeTo(Schema.Int.check(Schema.isBetween({ minimum, maximum })), SchemaTransformation.numberFromString),
      Schema.withDecodingDefaultTypeKey(Effect.succeed(byDefault)));
// A flag that is on only as "1".
const flag = (description: string) => Schema.optionalKey(Schema.Literal("1").annotate({ description }));
const wait = flag("1 waits up to 25 seconds for the result; without it, the request doesn't wait.");

// A staging or a test run that answers 200 once it has finished, with
// ?wait=1, and 202 while it runs; each status's schema takes only its own
// bodies, so encoding picks the status.
const Staged = Schema.Struct({ commit: CommitSha, toolset: Schema.NullOr(ToolsetSummary) });
const isStaged = (staged: typeof Staged.Type) => staged.toolset !== null && staged.toolset.status !== "validating";
const Ran = Schema.NullOr(TestRun);
const hasEnded = (ran: typeof Ran.Type) => ran !== null && ran.run.status !== "running";

export const curatorEndpoints = HttpApiGroup.make("curator", { topLevel: true })
  .add(HttpApiEndpoint.get("listHubs", "/api/v1/hubs", { success: HubsResponse })
    .annotate(OpenApi.Summary, "List the caller's hubs, with each hub's summary"))
  .add(HttpApiEndpoint.post("registerHub", "/api/v1/hubs", {
    payload: RegisterRequest,
    success: Schema.Struct({ hub: HubRecordJson }).pipe(HttpApiSchema.status(201)),
    // HubNotFound: the repository isn't one the caller can see.
    error: [HttpApiError.BadRequestNoContent, HubNotFound, NotRepoConfigurer, HubAlreadyRegistered, HubRepoConflict, InvalidListingTransition],
  }).annotate(OpenApi.Summary, "Register a WarmHub repository as a hub"))
  .add(HttpApiEndpoint.post("rebuildIndex", "/api/v1/index/rebuild", {
    success: Schema.Struct({
      hubs: Schema.Array(Schema.Struct({ id: Schema.String, name: Schema.String, toolsets: Schema.Array(Schema.String), rowsWritten: Schema.Int })),
    }),
    error: [NotOperator, IntentIndexRebuildRefused],
  }).annotate(OpenApi.Summary, "Rebuild the intent index from D1 (operator only)"))
  .add(HttpApiEndpoint.post("importHubSecrets", "/api/v1/secrets/import", {
    success: Schema.Struct({
      hubs: Schema.Array(Schema.Struct({ id: Schema.String, name: Schema.String, ok: Schema.Boolean, detail: Schema.optionalKey(Schema.String) })),
      notice: Schema.optionalKey(Schema.String),
    }),
    error: [NotOperator],
  }).annotate(OpenApi.Summary, "Import every hub's rows from the hub secret table into its HubDurableObject (operator only)"))
  .add(HttpApiEndpoint.post("startHubJob", "/api/v1/hub-jobs", {
    // 204 when there is no hub, so no job: nothing was started.
    payload: Schema.Struct({ kind: HubJobKind }), success: [Schema.Struct({ job: HubJobJson }).pipe(HttpApiSchema.status(202)), HttpApiSchema.NoContent], error: [HttpApiError.BadRequestNoContent, NotOperator],
  }).annotate(OpenApi.Summary, "Start a hub job, which runs one step on every hub (operator only)"))
  .add(HttpApiEndpoint.post("resumeHubJob", "/api/v1/hub-jobs/:job/resume", {
    params: { job: HubJobId }, success: Schema.Struct({ job: HubJobJson }).pipe(HttpApiSchema.status(202)), error: [HttpApiError.BadRequestNoContent, NotOperator, NotFound],
  }).annotate(OpenApi.Summary, "Send each pending step of a hub job to the queue again (operator only)"))
  .add(HttpApiEndpoint.get("getHubJob", "/api/v1/hub-jobs/:job", {
    params: { job: HubJobId }, success: Schema.Struct({ job: HubJobJson }), error: [HttpApiError.BadRequestNoContent, NotOperator, NotFound],
  }).annotate(OpenApi.Summary, "Read a hub job's progress: each hub's step (operator only)"))
  .add(HttpApiEndpoint.get("listListingApprovals", "/api/v1/listing-approvals", { success: Schema.Struct({ hubs: Schema.Array(HubSummary) }), error: [NotOperator] })
    .annotate(OpenApi.Summary, "List the hubs whose first listing awaits approval (operator only)"))
  .add(HttpApiEndpoint.post("listHub", "/api/v1/hubs/:hub/listing", {
    params: { hub: HubId }, success: Schema.Struct({ hub: HubRecordJson }), error: [...OneHub, NotOperator, HubArchived, InvalidListingTransition],
  }).annotate(OpenApi.Summary, "List one hub for consumers: approve its first listing, or relist it"))
  .add(HttpApiEndpoint.delete("unlistHub", "/api/v1/hubs/:hub/listing", {
    params: { hub: HubId }, payload: WithReason, success: Schema.Struct({ hub: HubRecordJson }), error: [HttpApiError.BadRequestNoContent, ...OneHub, NotOperator, HubArchived, InvalidListingTransition],
  }).annotate(OpenApi.Summary, "Unlist one hub, or reject its first listing"))
  .add(HttpApiEndpoint.put("setHubAudience", "/api/v1/hubs/:hub/audience", {
    params: { hub: HubId }, payload: Schema.Struct({ kind: Schema.Literals(["everyone", "org"]) }),
    success: Schema.Struct({ hub: HubRecordJson }), error: [HttpApiError.BadRequestNoContent, ...OneHub, NotRepoConfigurer, HubArchived, AudienceRefused],
  }).annotate(OpenApi.Summary, "Set the hub's audience: every signed-in consumer, or the members of its linked repository's org")
    .annotate(OpenApi.Description, "Needs repo:configure on the hub's linked repository, checked fresh; no org admin approves, for now. `org` means whoever can read the linked repository, and is refused for a public one. A hub limited to an org is still sent, with its listing, to the platform's processors, Jev and turbopuffer."))
  .add(HttpApiEndpoint.delete("archiveHub", "/api/v1/hubs/:hub", {
    params: { hub: HubId }, payload: WithReason, success: Schema.Struct({ hub: HubRecordJson, notice: Schema.optionalKey(Schema.String) }), error: [HttpApiError.BadRequestNoContent, ...OneHub, InvalidListingTransition],
  }).annotate(OpenApi.Summary, "Archive one hub"))
  .add(HttpApiEndpoint.put("grantHubSecret", "/api/v1/hubs/:hub/secrets/warmhub", {
    params: { hub: HubId }, payload: GrantRequest, success: Schema.Struct({ secret: HubSecretState }), error: [HttpApiError.BadRequestNoContent, ...OneHub, NotRepoConfigurer, HubArchived, ToolsetNotFound, HubTokenRefused],
  }).annotate(OpenApi.Summary, "Grant the hub's WarmHub token for a toolset, with the caller's own token"))
  .add(HttpApiEndpoint.delete("revokeHubSecret", "/api/v1/hubs/:hub/secrets/warmhub", {
    params: { hub: HubId }, success: Schema.Struct({ revokedInWarmHub: Schema.Boolean, notice: Schema.optionalKey(Schema.String) }), error: OneHub,
  }).annotate(OpenApi.Summary, "Revoke every WarmHub token of the hub"))
  .add(HttpApiEndpoint.put("connectHubToken", "/api/v1/hubs/:hub/secrets/hub-token", {
    params: { hub: HubId }, success: Schema.Struct({ secret: HubSecretState }), error: [...OneHub, NotRepoConfigurer, HubArchived, HubTokenRefused],
  }).annotate(OpenApi.Summary, "Connect the hub's Hub token: mint it with the caller's own token"))
  .add(HttpApiEndpoint.delete("disconnectHubToken", "/api/v1/hubs/:hub/secrets/hub-token", {
    params: { hub: HubId }, success: Schema.Struct({ revokedInWarmHub: Schema.Boolean, notice: Schema.optionalKey(Schema.String) }), error: OneHub,
  }).annotate(OpenApi.Summary, "Disconnect the hub's Hub token: delete it, and revoke it with the token that minted it"))
  .add(HttpApiEndpoint.get("listHubSecrets", "/api/v1/hubs/:hub/secrets", {
    params: { hub: HubId }, success: HubSecretsSummary, error: OneHub,
  }).annotate(OpenApi.Summary, "List the hub's secrets: its WarmHub tokens, API keys and Hub token, never a value"))
  .add(HttpApiEndpoint.put("setApiKey", "/api/v1/hubs/:hub/secrets/:host", {
    params: { hub: HubId, host: ApiKeyHost }, payload: ApiKeyRequest, success: Schema.Struct({ apiKey: ApiKeyState }), error: [HttpApiError.BadRequestNoContent, ...OneHub, HubArchived, ApiKeyLimitReached],
  }).annotate(OpenApi.Summary, "Set the hub's API key for one header or query parameter of one host, sent there to that host only"))
  .add(HttpApiEndpoint.delete("removeApiKey", "/api/v1/hubs/:hub/secrets/:host", {
    params: { hub: HubId, host: ApiKeyHost }, query: {
      account: Schema.optionalKey(ApiKeyAccount), allAccounts: flag("1 removes every account's keys; without it, only the account named, or default's."),
      header: Schema.optionalKey(ApiKeyHeader), param: Schema.optionalKey(ApiKeyParam),
    }, success: Schema.Struct({ removed: Schema.Boolean }), error: [HttpApiError.BadRequestNoContent, HttpApiError.BadRequest, ...OneHub],
  }).annotate(OpenApi.Summary, "Remove the hub's API key for one header or query parameter of one account of one host, or with neither every key of the account; default's unless an account is named, or every account's with allAccounts=1"))
  .add(HttpApiEndpoint.get("listToolsets", "/api/v1/hubs/:hub/toolsets", {
    params: { hub: HubId }, success: Schema.Struct({ hub: HubRecordJson, toolsets: Schema.Array(ToolsetSummary) }), error: OneHub,
  }).annotate(OpenApi.Summary, "List the hub's newest 50 toolsets"))
  // 200 once staging has finished, with ?wait=1; 202 while it runs.
  .add(HttpApiEndpoint.post("stageToolset", "/api/v1/hubs/:hub/toolsets", {
    params: { hub: HubId }, query: { wait }, payload: StageRequest,
    success: [Staged.check(Schema.makeFilter(isStaged)).pipe(HttpApiSchema.status(200)), Staged.check(Schema.makeFilter((s) => !isStaged(s))).pipe(HttpApiSchema.status(202))],
    error: [HttpApiError.BadRequestNoContent, ...OneHub, HubArchived, ToolsetNotFound],
  }).annotate(OpenApi.Summary, "Stage a commit of the hub's toolset repository"))
  // Any curator of the hub, or the operator, gets a push token.
  .add(HttpApiEndpoint.post("issuePushToken", "/api/v1/hubs/:hub/push-token", {
    params: { hub: HubId }, payload: PushTokenRequest,
    success: Schema.Struct({ remote: Schema.String, token: Schema.String, expiresAt: Schema.String, templateRemote: Schema.String, templateToken: Schema.String }),
    error: [HttpApiError.BadRequestNoContent, ...OneHub, HubArchived],
  }).annotate(OpenApi.Summary, "Issue a push token for the hub's toolset repository"))
  .add(HttpApiEndpoint.delete("revokePushTokens", "/api/v1/hubs/:hub/push-tokens", { params: { hub: HubId }, success: Schema.Struct({ revoked: Schema.Int }), error: OneHub })
    .annotate(OpenApi.Summary, "Revoke every push token of the hub"))
  // 200 once the run has ended, with ?wait=1; 202 while it runs.
  .add(HttpApiEndpoint.post("startTestRun", "/api/v1/hubs/:hub/toolsets/:commit/test-runs", {
    params: { hub: HubId, commit: CommitSha }, query: { wait }, payload: TestRunRequest,
    success: [Ran.check(Schema.makeFilter(hasEnded)).pipe(HttpApiSchema.status(200)), Ran.check(Schema.makeFilter((r) => !hasEnded(r))).pipe(HttpApiSchema.status(202))],
    error: [HttpApiError.BadRequestNoContent, ...OneHub, HubArchived, ToolsetNotFound, HubSecretMissing],
  }).annotate(OpenApi.Summary, "Run a toolset's examples or golden questions as test runs"))
  .add(HttpApiEndpoint.get("getTestRun", "/api/v1/hubs/:hub/test-runs/:runId", { params: { hub: HubId, runId: TestRunId }, success: TestRun, error: [HttpApiError.BadRequestNoContent, ...OneHub, NotFound] })
    .annotate(OpenApi.Summary, "Read one test run with its results"))
  // A curator's request for a repair round of a toolset the hub agent built:
  // the hub agent offers the round as an authorize, or tells the curator why
  // not. 202 with the hub event the request became. See Design > Hub agent,
  // "Repair rounds".
  .add(HttpApiEndpoint.post("requestRepair", "/api/v1/hubs/:hub/toolsets/:commit/repair", {
    params: { hub: HubId, commit: CommitSha }, success: RequestRepairResponse.pipe(HttpApiSchema.status(202)),
    error: [HttpApiError.BadRequestNoContent, ...OneHub, NotRepoConfigurer, HubArchived, ToolsetNotFound],
  }).annotate(OpenApi.Summary, "Ask the hub agent for a repair round of a toolset it built"))
  .add(HttpApiEndpoint.post("activateToolset", "/api/v1/hubs/:hub/toolsets/:commit/activate", {
    params: { hub: HubId, commit: CommitSha }, payload: ActivateRequest, success: Schema.Struct({ hub: HubRecordJson, toolset: ToolsetSummary }),
    error: [HttpApiError.BadRequestNoContent, ...OneHub, HubArchived, ToolsetNotFound, ActivationRefused, InvalidListingTransition, ToolsetChanged],
  }).annotate(OpenApi.Summary, "Activate a staged toolset"))
  .add(HttpApiEndpoint.get("listActivations", "/api/v1/hubs/:hub/activations", {
    params: { hub: HubId }, success: Schema.Struct({ activations: Schema.Array(ActivationSummary) }), error: OneHub,
  }).annotate(OpenApi.Summary, "List the hub's newest 50 toolset activations, rollbacks included"))
  .add(HttpApiEndpoint.get("getDemand", "/api/v1/hubs/:hub/demand", {
    params: { hub: HubId }, query: { days: wholeNumber(1, 90, 7, "Days back to read") },
    success: DemandResponse, error: [HttpApiError.BadRequestNoContent, ...OneHub],
  }).annotate(OpenApi.Summary, "Read the hub's unmet demand"))
  // The hub agent's activity, read from the hub's HubAgent Durable Object:
  // its newest agent actions and Interactions, one agent action's curated
  // transcript, and a Curator reply to one Interaction. See Design >
  // Exchange, "The hub agent's activity".
  .add(HttpApiEndpoint.get("getAgentActivity", "/api/v1/hubs/:hub/agent", {
    params: { hub: HubId }, query: { limit: wholeNumber(ACTIVITY_LIMIT.minimum, ACTIVITY_LIMIT.maximum, ACTIVITY_LIMIT.byDefault, "How many agent actions, and how many closed Interactions") },
    success: AgentActivityResponse, error: [HttpApiError.BadRequestNoContent, ...OneHub],
  }).annotate(OpenApi.Summary, "Read the hub agent's newest agent actions and Interactions"))
  .add(HttpApiEndpoint.get("getAgentAction", "/api/v1/hubs/:hub/agent/actions/:actionId", {
    params: { hub: HubId, actionId: ActionId }, success: AgentActionTranscript, error: [HttpApiError.BadRequestNoContent, ...OneHub, NotFound],
  }).annotate(OpenApi.Summary, "Read one agent action and its curated transcript"))
  .add(HttpApiEndpoint.post("replyToInteraction", "/api/v1/hubs/:hub/agent/interactions/:interactionId/reply", {
    params: { hub: HubId, interactionId: InteractionId }, payload: CuratorReply, success: [CuratorReplyResponse.pipe(HttpApiSchema.status(200)), CuratorReplyPending.pipe(HttpApiSchema.status(202))],
    error: [HttpApiError.BadRequestNoContent, ReplyInvalid, ...OneHub, NotRepoConfigurer, HubArchived, NotFound, InteractionClosed],
  }).annotate(OpenApi.Summary, "Reply to one Interaction, once"))
  // The caller's read marks on the hub's informs, which Curator Studio sends
  // as its "For you to read" shows them. See Design > Hub agent, "Pausing
  // for the curator".
  .add(HttpApiEndpoint.post("markInteractionsRead", "/api/v1/hubs/:hub/agent/interactions/read", {
    params: { hub: HubId }, payload: MarkInteractionsRead, success: HttpApiSchema.NoContent,
    error: [HttpApiError.BadRequestNoContent, ...OneHub, NotRepoConfigurer],
  }).annotate(OpenApi.Summary, "Mark the hub's informs read, for the caller"))
  // The operator's read of one agent action's trajectory: every conversation
  // of its tree it ran, whole. See Design > Hub agent, "Where a build's time
  // went".
  .add(HttpApiEndpoint.get("agentTrajectory", "/api/v1/hubs/:hub/agent/actions/:actionId/trajectory", {
    params: { hub: HubId, actionId: ActionId }, success: AgentTrajectory, error: [HttpApiError.BadRequestNoContent, NotOperator, ...OneHub, NotFound],
  }).annotate(OpenApi.Summary, "Read one agent action's whole trajectory, by build stage and lane (operator only)"))
  // The operator's requeue of one hub event in the hub's Hub inbox, under a
  // new eventId, such as a hub.registered whose agent action gave up. See
  // Design > Operations, "Requeuing a hub event".
  .add(HttpApiEndpoint.post("requeueHubEvent", "/api/v1/hubs/:hub/agent/events/:eventId/requeue", {
    params: { hub: HubId, eventId: HubEvent.fields.eventId }, success: RequeueResponse.pipe(HttpApiSchema.status(202)),
    error: [HttpApiError.BadRequestNoContent, NotOperator, ...OneHub, HubArchived, NotFound],
  }).annotate(OpenApi.Summary, "Requeue one hub event of the hub's agent under a new eventId (operator only)"))
  // The operator's write of the hub's own hub agent switches,
  // `hub:<Hub ID>` and `dailyCapUsd:<Hub ID>`, in HUB_AGENT_SWITCHES. KV
  // carries a write to every reader within about a minute. See Design >
  // Operations, "Hub agent switches".
  .add(HttpApiEndpoint.put("setHubAgentSwitch", "/api/v1/hubs/:hub/agent/switch", {
    params: { hub: HubId }, payload: SetHubAgentSwitch, success: HubAgentSwitchState,
    error: [HttpApiError.BadRequestNoContent, NotOperator, ...OneHub, HubArchived],
  }).annotate(OpenApi.Summary, "Turn the hub agent on or off for the hub, and set its daily cap; takes effect within about a minute (operator only)"))
  // Every Interaction waiting on the curator, across the hubs the caller
  // curates, for Curator Studio's "needs you". See Design > Exchange, "The
  // hub agent's activity".
  .add(HttpApiEndpoint.get("listWaitingInteractions", "/api/v1/agent/interactions", { success: WaitingInteractionsResponse })
    .annotate(OpenApi.Summary, "List the Interactions waiting on the caller, on the hubs they curate"))
  // WarmHub reads in the hub's readable repositories, with the caller's own
  // token, a batch at a time, each read's result beside the others. See
  // Design > Exchange, "Reading a hub's repositories".
  .add(HttpApiEndpoint.post("readWarmHub", "/api/v1/hubs/:hub/warmhub/reads", {
    params: { hub: HubId }, payload: ReadsRequest, success: ReadsResponse, error: [HttpApiError.BadRequestNoContent, ...OneHub],
  }).annotate(OpenApi.Summary, "Read WarmHub in the hub's readable repositories, with the caller's own token"))
  // A draft manifest's intent routing, run by the HubRouter's own routing
  // code on the hub's router model, for each question. See Design > Hub
  // agent, "The route check is the HubRouter's own".
  .add(HttpApiEndpoint.post("checkRouting", "/api/v1/hubs/:hub/routing-check", {
    params: { hub: HubId }, payload: RoutingCheckRequest, success: RoutingCheckResponse, error: [HttpApiError.BadRequestNoContent, ...OneHub],
  }).annotate(OpenApi.Summary, "Route questions on a draft of the hub's intents, as the HubRouter would"))
  .add(HttpApiEndpoint.get("listEvents", "/api/v1/events", {
    query: {
      hub: Schema.optionalKey(HubId.annotate({ description: "One hub's events, by its Hub ID." })),
      limit: wholeNumber(1, 200, 50, "How many events"),
      tests: flag("1 includes test runs' events; without it, they're left out."),
    },
    success: EventsResponse, error: [HttpApiError.BadRequestNoContent, HubNotFound],
  }).annotate(OpenApi.Summary, "List recent events of the caller's hubs"))
  .add(HttpApiEndpoint.get("listGolden", "/api/v1/golden", { success: GoldenResponse })
    .annotate(OpenApi.Summary, "List the golden questions of the caller's hubs"))
  // The contract hash of the Curator API this Exchange serves, which a
  // client built from another contract compares with its own.
  .add(HttpApiEndpoint.get("getContract", "/api/v1/contract", { success: Schema.Struct({ hash: Schema.String }) })
    .annotate(OpenApi.Summary, "Read the contract hash of the Curator API this Exchange serves"));

// The Curator API around its endpoints, with the middleware a side adds: the
// query and the body refuse a key they don't declare; headers keep
// Schema's defaults, since every request carries transport headers the
// contract doesn't name. See Design > Code standards, "An unreleased surface
// has no wire to keep".
export const curatorApiOf = <const G extends HttpApiGroup.Constraint>(group: G) => HttpApi.make("curator-api").add(group)
  .annotate(HttpApi.QueryParseOptions, { onExcessProperty: "error" })
  .annotate(HttpApi.PayloadParseOptions, { onExcessProperty: "error" })
  .annotateMerge(OpenApi.annotations({
  title: "WarmHub answer exchange: Curator API",
  version: "1.0.0",
  description: "What Curator Studio reads and writes: a curator's hubs, their toolsets, test runs, listing, demand, events, golden questions and the hub agent's activity. Every request needs a WarmHub bearer token. See Design > Exchange, \"Curator API\".",
}));

// The Curator API as a client calls it.
export const CuratorApi = curatorApiOf(curatorEndpoints.middleware(Bearer));

// The OpenAPI document, as openapi/curator-api.json holds it. Its SHA-256,
// in lowercase hex, is the contract hash: the Exchange serves its own at GET
// /api/v1/contract, and a client built from this contract carries its own,
// so a client and an Exchange built from different contracts can find out
// before either misreads the other's wire. It covers what the document renders:
// a custom filter, such as ActivateRequest's, isn't in it, so a change to
// one alone leaves the hash as it was. See Design > Exchange, "Curator API".
export const curatorOpenApi = () => `${JSON.stringify(OpenApi.fromApi(CuratorApi), null, 2)}\n`;
